
import { useEffect, useMemo, useState } from 'react';
import { CircleAlert, CircleCheck, Library } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Pill,
  PlayButton,
  SearchInput,
  Select,
  SkeletonRows,
  Table,
  tableCell,
  useAudioPreview,
} from '../../components/ui';
import { useCurrentUser } from '../../hooks';
import { LANGUAGES } from '../../lib/languages';
import type { SharedVoice } from '../../types/api';
import { formatCount, languageLabel } from '../tts/format';
import { speaksButNotNative } from '../tts/voiceOptions';
import { useAddVoiceFromLibrary, useVoiceLibrary } from './catalogQueries';
import {
  accentLabel,
  ageLabel,
  genderLabel,
  traitLine,
  purposeLabel,
  VOICE_AGES,
  VOICE_GENDERS,
} from './vocab';
import styles from './VoicesPage.module.css';

const PAGE_SIZE = 30;

function describeTimbre(voice: SharedVoice): string {
  return traitLine([accentLabel(voice.accent), ageLabel(voice.age), purposeLabel(voice.use_case)]);
}

export function VoiceLibraryTab({ onOpenOurs }: { onOpenOurs: () => void }) {
  const { can } = useCurrentUser();
  const canAdd = can('editor');

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [language, setLanguage] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [accent, setAccent] = useState('');
  const [accentDebounced, setAccentDebounced] = useState('');

  const [pending, setPending] = useState<SharedVoice | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  const audio = useAudioPreview();
  const add = useAddVoiceFromLibrary();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const timer = window.setTimeout(() => setAccentDebounced(accent.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [accent]);

  const library = useVoiceLibrary(
    {
      q: debounced || undefined,
      language: language || undefined,
      gender: gender || undefined,
      age: age || undefined,
      accent: accentDebounced || undefined,
      page_size: PAGE_SIZE,
    },
    true,
  );

  const pages = library.data?.pages;

  // страницы приходят от провайдера и на границах повторяются: один и тот же
  // голос в двух подряд идущих ответах ломает ключи строк
  const voices = useMemo(() => {
    const seen = new Map<string, SharedVoice>();
    for (const chunk of pages ?? []) {
      for (const voice of chunk.voices) {
        seen.set(`${voice.public_owner_id}:${voice.voice_id}`, voice);
      }
    }
    return [...seen.values()];
  }, [pages]);

  const total = pages?.[0]?.total ?? 0;

  const confirmAdd = () => {
    if (!pending) return;
    add.mutate(
      {
        public_owner_id: pending.public_owner_id,
        voice_id: pending.voice_id,
        name: pending.name,
      },
      {
        onSuccess: (result) => {
          setPending(null);
          setAdded(result.name);
        },
      },
    );
  };

  return (
    <>
      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          value={query}
          onChange={setQuery}
          placeholder="Поиск по публичной библиотеке"
          ariaLabel="Поиск по публичной библиотеке голосов"
        />

        <Select
          className={styles.filterSelect}
          size="sm"
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
          aria-label="Язык голоса"
        >
          <option value="">Любой язык</option>
          {LANGUAGES.map((item) => (
            <option key={item.code} value={item.code}>
              {item.label}
            </option>
          ))}
        </Select>

        <Select
          className={styles.filterSelect}
          size="sm"
          value={gender}
          onChange={(event) => setGender(event.target.value)}
          aria-label="Пол голоса"
        >
          <option value="">Любой пол</option>
          {VOICE_GENDERS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>

        <Select
          className={styles.filterSelect}
          size="sm"
          value={age}
          onChange={(event) => setAge(event.target.value)}
          aria-label="Возраст голоса"
        >
          <option value="">Любой возраст</option>
          {VOICE_AGES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>

        {/* список акцентов у провайдера открытый и нигде не публикуется, поэтому поле,
            а не выпадающий список */}
        <Input
          className={styles.filterInput}
          size="sm"
          value={accent}
          onChange={(event) => setAccent(event.target.value)}
          placeholder="Акцент: american, british…"
          aria-label="Акцент голоса"
          maxLength={40}
        />

        <span className={styles.toolbarActions}>
          <span className={styles.panelNote}>
            {total > 0 ? `нашлось ${formatCount(total)}` : 'идёт поиск'}
          </span>
        </span>
      </div>

      {!canAdd && (
        <div className={styles.notice}>
          <CircleAlert size={14} strokeWidth={1.5} className={styles.noticeIcon} />
          <span>
            Роль «наблюдатель»: искать и слушать можно, добавлять нельзя.
          </span>
        </div>
      )}

      {added && (
        <div className={styles.notice}>
          <CircleCheck size={14} strokeWidth={1.5} className={styles.noticeIcon} />
          <span>
            Голос «{added}» добавлен в аккаунт и уже виден во вкладке «Наши голоса», там же
            его можно привязать к персонажу.
          </span>
          <span className={styles.grow} />
          <Button size="sm" onClick={onOpenOurs}>
            Открыть «Наши голоса»
          </Button>
        </div>
      )}
      {add.error && <ErrorBanner error={add.error} />}

      {library.error && (
        <ErrorBanner error={library.error} onRetry={() => void library.refetch()} />
      )}

      {library.isPending && !library.error && (
        <Card>
          <div className={styles.pad}>
            <SkeletonRows rows={6} />
          </div>
        </Card>
      )}

      {!library.isPending && !library.error && voices.length === 0 && (
        <Card>
          <EmptyState
            icon={<Library size={18} strokeWidth={1.5} />}
            title="Ничего не нашлось"
            description="Поиск идёт по названию и описанию на английском: «news», «narration», «deep male». Русские слова тут почти не встречаются, попробуйте снять фильтры."
            action={
              query || language || gender || age || accent ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setQuery('');
                    setLanguage('');
                    setGender('');
                    setAge('');
                    setAccent('');
                  }}
                >
                  Сбросить поиск
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}

      {voices.length > 0 && (
        <Card>
          <Table bare className={styles.voiceTable}>
            <thead>
              <tr>
                <th className={styles.colPlay} />
                <th className={styles.libName}>голос</th>
                <th className={styles.colLang}>язык</th>
                <th className={styles.libTimbre}>тембр</th>
                <th>описание</th>
                <th className={styles.libAdd} />
              </tr>
            </thead>
            <tbody>
              {voices.map((voice) => {
                const key = `shared:${voice.public_owner_id}:${voice.voice_id}`;
                const playing = audio.playingKey === key;
                const timbre = describeTimbre(voice);
                const sex = genderLabel(voice.gender);
                const foreign = speaksButNotNative(voice, language || null);
                const lang =
                  traitLine([
                    foreign ? languageLabel(language) : null,
                    voice.language
                      ? foreign
                        ? `родной ${languageLabel(voice.language).toLowerCase()}`
                        : languageLabel(voice.language)
                      : null,
                    voice.locale && voice.locale !== voice.language ? voice.locale : null,
                  ]) || 'нет';
                return (
                  <tr key={key}>
                    <td>
                      <PlayButton
                        playing={playing}
                        disabled={!voice.preview_url}
                        onClick={() =>
                          voice.preview_url ? audio.toggle(key, voice.preview_url) : undefined
                        }
                        title={
                          voice.preview_url
                            ? playing
                              ? 'Остановить'
                              : 'Прослушать образец'
                            : 'Провайдер не приложил образец к этому голосу'
                        }
                        ariaLabel={
                          playing ? 'Остановить образец' : `Прослушать образец: ${voice.name}`
                        }
                      />
                    </td>

                    <td>
                      <span className={styles.name}>
                        <span
                          className={styles.nameText}
                          title={`${voice.name}\nid в публичной библиотеке: ${voice.voice_id}`}
                        >
                          {voice.name}
                        </span>
                        {sex && <Pill>{sex}</Pill>}
                      </span>
                    </td>

                    <td className={tableCell.mono} title={lang}>
                      {lang}
                    </td>

                    <td>
                      <span className={styles.cell} title={timbre || undefined}>
                        {timbre}
                      </span>
                    </td>

                    <td>
                      <span className={styles.cell} title={voice.description || undefined}>
                        {voice.description}
                      </span>
                    </td>

                    <td className={tableCell.actions}>
                      {voice.already_added ? (
                        <Pill tone="ok">уже в аккаунте</Pill>
                      ) : canAdd ? (
                        <Button
                          size="xs"
                          onClick={() => setPending(voice)}
                          disabled={add.isPending}
                          title="Голос станет доступен всей команде"
                        >
                          Добавить к себе
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className={styles.pager}>
            <span className={styles.pagerText}>
              показано {formatCount(voices.length)}
              {total > 0 ? ` из ${formatCount(total)}` : ''}
            </span>
            <span className={styles.grow} />
            {library.hasNextPage && (
              <Button
                size="sm"
                onClick={() => void library.fetchNextPage()}
                disabled={library.isFetchingNextPage}
              >
                {library.isFetchingNextPage ? 'Грузим…' : 'Показать ещё'}
              </Button>
            )}
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        title="Добавить голос в общий аккаунт?"
        body={`Голос «${pending?.name ?? ''}» появится в «Наших голосах» и станет доступен всем.`}
        confirmLabel="Добавить"
        busy={add.isPending}
        onConfirm={confirmAdd}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
